// Code generated by protoc-gen-ts_proto. DO NOT EDIT.
// versions:
//   protoc-gen-ts_proto  v2.11.7
//   protoc               v5.29.6
// source: eligibility.proto

export const protobufPackage = 'compasspb';

/** EligibilityCheckStatus represents the current state of an eligibility verification request. */
export enum EligibilityCheckStatus {
  /** ELIGIBILITY_CHECK_STATUS_UNSPECIFIED - Default value, should not be used */
  ELIGIBILITY_CHECK_STATUS_UNSPECIFIED = 0,
  /** ELIGIBILITY_CHECK_STATUS_CREATED - Eligibility check has been created but not yet started */
  ELIGIBILITY_CHECK_STATUS_CREATED = 1,
  /** ELIGIBILITY_CHECK_STATUS_IN_PROGRESS - Eligibility check is currently being processed */
  ELIGIBILITY_CHECK_STATUS_IN_PROGRESS = 2,
  /** ELIGIBILITY_CHECK_STATUS_COMPLETED - Eligibility check completed successfully */
  ELIGIBILITY_CHECK_STATUS_COMPLETED = 3,
  /** ELIGIBILITY_CHECK_STATUS_FAILED - Eligibility check failed due to an error */
  ELIGIBILITY_CHECK_STATUS_FAILED = 4,
  /** ELIGIBILITY_CHECK_STATUS_CANCELLED - Eligibility check was cancelled by user or system */
  ELIGIBILITY_CHECK_STATUS_CANCELLED = 5,
  /** ELIGIBILITY_CHECK_STATUS_SKIPPED - Eligibility check was skipped (e.g., not required for this case) */
  ELIGIBILITY_CHECK_STATUS_SKIPPED = 6,
}

/**
 * EligibilityCheckError represents an error that occurred during eligibility verification.
 * These errors can come from the insurance payer or from validation issues with the input data.
 */
export interface EligibilityCheckError {
  /** The field name that caused the error (e.g., "member_id", "date_of_birth") */
  field?: string | undefined;
  /** Human-readable description of the error */
  description?: string | undefined;
  /** Recommended action to resolve the error (e.g., "Verify member ID with insurance card") */
  follow_up_action?: string | undefined;
}

/**
 * EligibilityCheck represents an insurance eligibility verification request and its results.
 * This tracks the verification process of a patient's insurance coverage and benefits.
 */
export interface EligibilityCheck {
  /** Unique identifier for this eligibility check (MongoDB ObjectID) */
  id?: string | undefined;
  /** Current status of the eligibility verification */
  status?: EligibilityCheckStatus | undefined;
  /** Case record ID this eligibility check is associated with */
  caserecord_id?: string | undefined;
  /** Timestamp when the eligibility check was created */
  created_at?: string | undefined;
  /** Timestamp when the eligibility check was last updated */
  updated_at?: string | undefined;
  /** Input data provided for the eligibility check (e.g., member demographics, insurance info) */
  input_data?: { [key: string]: any } | undefined;
  /** Output data returned from the eligibility check (e.g., coverage details, copay amounts) */
  output_data?: { [key: string]: any } | undefined;
  /** Payer identifier (insurance company ID) for this eligibility check */
  payer_id?: string | undefined;
  /** Attempt number for this payer (used for retry tracking, starts at 1) */
  attempt?: number | undefined;
  /** List of errors encountered during eligibility verification */
  errors?: EligibilityCheckError[] | undefined;
  /** Case state indicating insurance type (e.g., \"primary\", \"secondary\", \"tertiary\") */
  case_state?: string | undefined;
  /** Payer name (insurance company name) for this eligibility check */
  payer_name?: string | undefined;
}

/** CreateEligibilityCheckRequest contains the parameters needed to create a new eligibility check. */
export interface CreateEligibilityCheckRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  input_data?: { [key: string]: any } | undefined;
  payer_id?: string | undefined;
  attempt?: number | undefined;
  payer_name?: string | undefined;
}

/** CreateEligibilityCheckResponse contains the newly created eligibility check. */
export interface CreateEligibilityCheckResponse {
  /** The newly created eligibility check */
  eligibility_check?: EligibilityCheck | undefined;
}

/** GetEligibilityCheckRequest contains the parameters to retrieve a specific eligibility check. */
export interface GetEligibilityCheckRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  eligibility_check_id?: string | undefined;
}

/** GetEligibilityCheckResponse contains the requested eligibility check details. */
export interface GetEligibilityCheckResponse {
  /** The requested eligibility check */
  eligibility_check?: EligibilityCheck | undefined;
}

/**
 * UpdateEligibilityCheckRequest contains the parameters to update an existing eligibility check.
 * All fields except IDs are optional and will only update if provided.
 */
export interface UpdateEligibilityCheckRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  eligibility_check_id?: string | undefined;
  status?: EligibilityCheckStatus | undefined;
  input_data?: { [key: string]: any } | undefined;
  output_data?: { [key: string]: any } | undefined;
  payer_id?: string | undefined;
  attempt?: number | undefined;
  errors?: EligibilityCheckError[] | undefined;
  payer_name?: string | undefined;
}

/** UpdateEligibilityCheckResponse contains the updated eligibility check. */
export interface UpdateEligibilityCheckResponse {
  /** The updated eligibility check */
  eligibility_check?: EligibilityCheck | undefined;
}

/**
 * GetCaseRecordEligibilityChecksRequest contains the parameters to retrieve all eligibility checks
 * for a specific case record, with optional filtering and pagination.
 */
export interface GetCaseRecordEligibilityChecksRequest {
  organization_id?: string | undefined;
  program_id?: string | undefined;
  caserecord_id?: string | undefined;
  limit?: number | undefined;
  start_after_id?: string | undefined;
  statuses?: string[] | undefined;
  payer_ids?: string[] | undefined;
  attempts?: number[] | undefined;
}

/** GetCaseRecordEligibilityChecksResponse contains the list of eligibility checks for a case record. */
export interface GetCaseRecordEligibilityChecksResponse {
  /** List of eligibility checks matching the request filters */
  eligibility_checks?: EligibilityCheck[] | undefined;
  /**
   * DEPRECATED: Total count is no longer populated. Use dedicated Count APIs instead.
   *
   * @deprecated
   */
  total_count?: number | undefined;
}

/**
 * EligibilityCheckService provides operations for insurance eligibility verification.
 * This service handles creating and managing eligibility checks for patients/members
 * to verify their insurance coverage and benefits.
 */
export interface EligibilityCheckService {
  /**
   * CreateEligibilityCheck creates a new eligibility check entry for a case record.
   * This initiates the process of verifying a patient's insurance eligibility and benefits.
   */
  createEligibilityCheck(
    request: CreateEligibilityCheckRequest,
  ): Promise<CreateEligibilityCheckResponse>;
  /**
   * GetEligibilityCheck retrieves details of a specific eligibility check by ID.
   * Returns comprehensive information about the eligibility verification status and results.
   */
  getEligibilityCheck(request: GetEligibilityCheckRequest): Promise<GetEligibilityCheckResponse>;
  /**
   * UpdateEligibilityCheck updates an existing eligibility check entry.
   * Use this to modify eligibility check details or update the verification status.
   */
  updateEligibilityCheck(
    request: UpdateEligibilityCheckRequest,
  ): Promise<UpdateEligibilityCheckResponse>;
  /**
   * GetCaseRecordEligibilityChecks retrieves all eligibility checks associated with a case record.
   * Returns a list of all eligibility verifications performed for the specified case.
   */
  getCaseRecordEligibilityChecks(
    request: GetCaseRecordEligibilityChecksRequest,
  ): Promise<GetCaseRecordEligibilityChecksResponse>;
}
